import { formatDate, DATE_FORMAT } from './date/formatDate';
import { formatBoolean } from './formatBoolean';
import { translateCode, resolveLabeledCode } from './translateCode';
import type { LabeledCodeInput } from './translateCode';
import type { FieldDef } from '../components/ui/sectionCard/types';
import type { VisibleField } from '../components/ui/FieldGrid';

type TFn = (key: string, options?: { defaultValue: string }) => string;

// DATE_FORMAT vit désormais dans lib/date/formatDate (à côté de formatDate) ;
// ré-exporté ici pour la compatibilité des imports existants.
export { DATE_FORMAT };

/**
 * Resolves a single field definition against a data object to produce a display string.
 * Generic over T so the same resolver works for both EmployeeRow and Contract data.
 */
export function resolveField<T extends Record<string, unknown>>(
  field: FieldDef<T>,
  data: T,
  t: TFn,
): string | null {
  if (field.compute) return field.compute(data) ?? null;
  if (!field.key) return null;
  const raw = data[field.key as string];
  if (raw === null || raw === undefined || raw === '') return null;
  switch (field.type) {
    case 'date': return formatDate(String(raw), DATE_FORMAT) || String(raw);
    case 'boolean':
      if (typeof raw !== 'boolean') return String(raw);
      return formatBoolean(raw, t);
    case 'labeled-code': {
      if (field.translatePrefix) {
        const value: LabeledCodeInput | string = typeof raw === 'string' ? raw : (raw as LabeledCodeInput);
        const result = resolveLabeledCode(t, field.translatePrefix, value);
        return result || null;
      }
      if (typeof raw === 'object' && raw !== null && 'code' in raw) {
        const lc = raw as LabeledCodeInput;
        return lc.libelle?.trim() || lc.code || null;
      }
      return String(raw);
    }
    default:
      if (field.translatePrefix) return translateCode(t, field.translatePrefix, String(raw));
      return String(raw);
  }
}

/**
 * Resolves an entire section's fields to VisibleField pairs for rendering.
 * Generic over T — works for any data shape (EmployeeRow, Contract, etc.).
 *
 * When a field carries `dynamicLabelKey`, the label is read from the data object
 * at that key (terrain-defined label). Falls back to t(labelKey) when absent.
 * The stable `id` comes from the field key or labelKey — never from the runtime
 * label — so React keys remain unique even when two PINs share the same terrain label.
 */
export function resolveSection<T extends Record<string, unknown>>(
  fields: FieldDef<T>[],
  data: T,
  t: TFn,
): VisibleField[] {
  return fields.map(f => {
    const dynamicLabel = f.dynamicLabelKey
      ? String(data[f.dynamicLabelKey as string] ?? '') || undefined
      : undefined;
    return {
      id: String(f.key ?? f.labelKey),
      label: dynamicLabel ?? t(f.labelKey),
      value: resolveField(f, data, t) ?? null,
      node: f.render ? f.render(data) : undefined,
      fullWidth: f.fullWidth,
    };
  });
}
